import dayjs from 'dayjs'
import {useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {MainPathologiesFormSchema} from '@/features/projects/formSchemas'
import {Diagnosis} from '@/features/projects/types'
import {DEFAULT_MAIN_PATHOLOGY_VALUES} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'

interface ProjectMainPathologiesFieldsProps {
    index: number
    disabledPathologies: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectMainPathologiesFields = ({
    index,
    disabledPathologies,
    showTrashButton,
    remove
}: ProjectMainPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<MainPathologiesFormSchema>()
    const {field} = useController({control, name: `mainPathologies.${index}`})

    const selectedPathology = useMemo(() => {
        return field.value
            ? {
                  id: field.value.diagnosisId,
                  description: field.value.description,
                  code: field.value.code
              }
            : null
    }, [field.value])

    const selectedStartDate = useMemo(() => {
        return field.value.startedAt ? dayjs(field.value.startedAt).toDate() : null
    }, [field.value])

    const handleSelectPathology = useCallback(
        (value: Diagnosis | null) => {
            if (!value) {
                field.onChange(DEFAULT_MAIN_PATHOLOGY_VALUES)
                return
            }

            field.onChange(
                {
                    ...field.value,
                    diagnosisId: value.id,
                    description: value.description,
                    code: value.code
                },
                {shouldDirty: true}
            )
        },
        [field.onChange, field.value]
    )

    const handleSelectStartDate = useCallback(
        (value: Date | null) => {
            field.onChange(
                {...field.value, startedAt: value ? dayjs(value).format('YYYY-MM-DD') : null},
                {
                    shouldDirty: true
                }
            )
        },
        [field.onChange, field.value]
    )

    return (
        <Flexbox fullWidth justify="space-between" gap={2.5}>
            <InputSelectPathology
                label={t('projects:pathology_number', {number: index + 1})}
                placeholder={t('projects:pathology_number_placeholder')}
                value={selectedPathology}
                onChange={handleSelectPathology}
                disabledOptions={disabledPathologies}
            />
            <Flexbox gap={1} align="flex-end">
                <InputDate
                    label={t('projects:start_date')}
                    placeholder={'GG/MM/AAAA'}
                    value={selectedStartDate}
                    onChange={handleSelectStartDate}
                    onMonthChange={handleSelectStartDate}
                    disableAfterDate={new Date()}
                />
                {showTrashButton && (
                    <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                        <Trash01Icon />
                    </Button>
                )}
            </Flexbox>
        </Flexbox>
    )
}
